<script lang="ts">
  import { RefreshCw } from '@lucide/svelte';
  import { onDestroy } from 'svelte';
  import { appendEventMarkers, chartModelFor, eventClassificationForMarker, eventClassificationOptions, eventWaveformAnalysisModel, eventWaveformModels, histogramModel, selectableEventTimestamps, type EventMarkerItem, type EventWaveformResponse } from '../lib/chart-model';
  import { eventAnalysisFromResponse } from '../lib/event-analysis';
  import { HttpError, rmsBatcher, requestManager } from '../lib/request-manager';
  import { buildPlotRequest, effectiveCursor, type PlotRequest } from '../lib/request-builders';
  import { panelSlide } from '../lib/panel-transition';
  import { normalizePlotSpec, parameterById, plotByKind } from '../lib/registry';
  import type { ApiEnvelope, ChartModel, ComparisonMode, Monitor, PanelNavigation, PlotAnalysisSource, PlotSpec, WorkspaceState } from '../lib/types';
  import { visibility } from '../lib/visibility';
  import { clampTimestamp, formatUtc, startOfUtcDay } from '../lib/time';
  import ChartHost from './ChartHost.svelte';
  import EventDrilldown from './EventDrilldown.svelte';
  import PlotPanelControls from './PlotPanelControls.svelte';
  import PlotPanelHeader from './PlotPanelHeader.svelte';

  export let plot: PlotSpec;
  export let workspace: WorkspaceState;
  export let monitors: Monitor[];
  export let comparisonMode: ComparisonMode;
  export let cursorPreview: number;
  export let index: number;
  export let count: number;
  export let onUpdate: (plot: PlotSpec) => void;
  export let onDuplicate: () => void;
  export let duplicateDisabled = false;
  export let onMove: (direction: -1 | 1) => void;
  export let onRemove: () => void;
  export let onFocused: () => void;
  export let keyboardActive = false;
  export let onNavigation: (navigation: PanelNavigation) => void;
  export let onRangeChange: (range: [number, number]) => void;
  export let onResetRange: () => void = () => undefined;
  export let onEventSelect: (monitorId: string, cursor: number) => void;
  export let onCursorPreview: (cursor: number) => void;
  export let analysisRequested = false;
  export let onAnalysisData: (plotId: string, source: PlotAnalysisSource | null) => void = () => undefined;
  export let waveformAnalysisRequested = false;
  export let onWaveformAnalysisData: (plotId: string, source: PlotAnalysisSource | null) => void = () => undefined;

  let visible = false;
  let loading = false;
  let error = '';
  let response: ApiEnvelope | null = null;
  let responseKey = '';
  let baseModel: ChartModel | null = null;
  let histogram: ChartModel | null = null;
  let eventData: Record<string, EventMarkerItem[]> | null = null;
  let eventLoading = false;
  let eventListError = '';
  let eventWaveforms: ChartModel[] = [];
  let eventWaveformData: { data: EventWaveformResponse; timestampSeconds: number } | null = null;
  let eventWaveformError = '';
  let eventWaveformMissing = false;
  let renderedEventNeutral = false;
  let eventWaveformLoading = false;
  let eventWaveformRange: [number, number] | undefined = undefined;
  let eventDrilldownElement: HTMLElement | null = null;
  let eventDrilldownLoadingMinHeight = 0;
  let controller: AbortController | null = null;
  let eventController: AbortController | null = null;
  let eventWaveformController: AbortController | null = null;
  let generation = 0;
  let eventGeneration = 0;
  let waveformGeneration = 0;
  let lastDataKey = '';
  let lastEventKey = '';
  let lastEventWaveformKey = '';

  $: parameter = parameterById(plot.parameterId);
  $: definition = plotByKind(plot.kind);
  $: effectiveMonitorId = plot.location.mode === 'override' && comparisonMode === 'multiple-locations' ? plot.location.monitorId : workspace.globalMonitorId;
  $: effectiveMonitor = monitors.find((item) => item.monitor_name_index === effectiveMonitorId);
  $: effectiveMonitorName = effectiveMonitor
    ? `${effectiveMonitor.primary_name}, ${effectiveMonitor.monitor_name_formatted}`
    : effectiveMonitorId;
  $: panelCursor = effectiveCursor(plot, workspace);
  $: rangeResetMonitor = plot.linkedTime
    ? monitors.find((item) => item.monitor_name_index === workspace.globalMonitorId)
    : effectiveMonitor;
  $: displayedRange = plot.linkedTime ? workspace.range : (plot.rangeOverride ?? workspace.range);
  $: resetZoomVisible = Boolean(
    definition.timeDomain === 'range'
    && rangeResetMonitor
    && (displayedRange[0] !== rangeResetMonitor.earliest_date || displayedRange[1] !== rangeResetMonitor.latest_date)
  );
  $: markerCursor = plot.linkedTime ? cursorPreview : panelCursor;
  $: dataRequest = effectiveMonitor ? buildPlotRequest(plot, effectiveMonitorId, workspace) : null;
  $: dataKey = dataRequest?.url ?? '';
  $: eventKey = plot.options.showEvents && definition.supportsEvents ? effectiveMonitorId : '';
  $: if ((visible || analysisRequested) && dataRequest && dataKey !== lastDataKey) void loadData(dataKey, dataRequest);
  $: if ((visible || waveformAnalysisRequested) && eventKey !== lastEventKey) void loadEvents(eventKey);
  $: if (response && responseKey === dataKey) {
    baseModel = chartModelFor(plot, response);
    histogram = histogramModel(plot, response);
    onAnalysisData(plot.id, {
      plotId: plot.id,
      monitorId: effectiveMonitorId,
      monitorName: effectiveMonitorName,
      model: baseModel
    });
  }
  $: eventClassificationChoices = eventData ? eventClassificationOptions(eventData, dataRequest?.range) : [];
  $: hiddenEventClassificationKeys = new Set(plot.hiddenEventClassifications ?? []);
  $: visibleEventClassificationCount = eventClassificationChoices.filter(({ key }) => !hiddenEventClassificationKeys.has(key)).length;
  $: navigableEventTimestamps = eventData
    ? selectableEventTimestamps(eventData, dataRequest?.range, hiddenEventClassificationKeys)
    : [];
  $: model = baseModel && eventData
    ? appendEventMarkers(baseModel, eventData, selectEvent, dataRequest?.range, hiddenEventClassificationKeys)
    : baseModel;
  $: selectedEvent = plot.options.showEvents
    && definition.supportsEvents
    && plot.selectedEvent?.monitorId === effectiveMonitorId
    && Number.isSafeInteger(plot.selectedEvent.timestamp)
    && plot.selectedEvent.timestamp >= 0
    ? plot.selectedEvent
    : undefined;
  $: selectedEventKey = selectedEvent ? `${selectedEvent.monitorId}|${selectedEvent.timestamp}` : '';
  $: selectedEventMarker = selectedEvent && eventData
    ? Object.values(eventData).flat().find((item) => Math.round(item.t / 1000) === selectedEvent.timestamp)
    : undefined;
  $: selectedEventClassificationKey = selectedEventMarker
    ? eventClassificationForMarker(selectedEventMarker.analysis).key
    : undefined;
  $: selectedEventClassificationHidden = Boolean(
    selectedEventClassificationKey && hiddenEventClassificationKeys.has(selectedEventClassificationKey)
  );
  $: if (
    (visible || waveformAnalysisRequested)
    && selectedEvent
    && selectedEventMarker
    && !selectedEventClassificationHidden
    && selectedEventKey !== lastEventWaveformKey
  ) void loadEventWaveforms(selectedEvent.timestamp, selectedEventKey);
  $: if (eventWaveformData && !eventWaveformMissing && renderedEventNeutral !== plot.options.showNeutral) {
    renderedEventNeutral = plot.options.showNeutral;
    eventWaveforms = eventWaveformModels(eventWaveformData.data, eventWaveformData.timestampSeconds, renderedEventNeutral);
    publishWaveformAnalysis(eventWaveforms, eventWaveformData.timestampSeconds);
  }
  $: eventAnalysis = eventWaveformData ? eventAnalysisFromResponse(eventWaveformData.data) : null;
  $: eventAnalysisHasDetails = Boolean(eventAnalysis && (
    eventAnalysis.recorderTrigger
    || eventAnalysis.waveformSignature
    || eventAnalysis.contextSummary
    || eventAnalysis.reason
    || eventAnalysis.phenomena.length
    || eventAnalysis.alternatives.length
    || eventAnalysis.evidence.length
    || eventAnalysis.limitations.length
    || eventAnalysis.noveltyStatus
    || eventAnalysis.versions.length
    || eventAnalysis.waveformMeasurementGroups.length
  ));
  $: if (!selectedEventKey && (eventWaveforms.length || eventWaveformLoading || lastEventWaveformKey)) resetEventWaveforms();
  $: if (plot.selectedEvent && !selectedEvent) clearSelectedEvent();
  $: if (selectedEvent && eventData !== null && !selectedEventMarker) clearSelectedEvent();
  $: if (selectedEvent && selectedEventClassificationHidden) clearSelectedEvent();
  $: panelLoading = loading || eventLoading || eventWaveformLoading;

  function update(changes: Partial<PlotSpec>) {
    onUpdate(normalizePlotSpec({ ...plot, ...changes }));
  }

  function setOption(name: string, enabled: boolean) {
    update({ options: { ...plot.options, [name]: enabled } });
  }

  function setEventClassificationVisible(key: string, visible: boolean) {
    const hidden = new Set(plot.hiddenEventClassifications ?? []);
    if (visible) hidden.delete(key);
    else hidden.add(key);
    const hidesSelectedEvent = !visible && key === selectedEventClassificationKey;
    if (hidesSelectedEvent) resetEventWaveforms();
    update({
      hiddenEventClassifications: Array.from(hidden).sort(),
      ...(hidesSelectedEvent ? { selectedEvent: undefined } : {})
    });
  }

  function showAllEventClassifications() {
    update({ hiddenEventClassifications: undefined });
  }

  function hideAllEventClassifications() {
    const hidden = new Set(plot.hiddenEventClassifications ?? []);
    for (const { key } of eventClassificationChoices) hidden.add(key);
    if (selectedEvent) resetEventWaveforms();
    update({
      hiddenEventClassifications: Array.from(hidden).sort(),
      ...(selectedEvent ? { selectedEvent: undefined } : {})
    });
  }

  function hasNoData(current: ChartModel): boolean {
    return Boolean(current.emptyMessage && current.series.every((series) => series.data.length === 0));
  }

  function hasCapturedEventWaveform(data: EventWaveformResponse): boolean {
    const channels = [
      data.L1_N__V, data.L2_N__V, data.L3_N__V, data.N_E__V,
      data.L1_Amp__A, data.L2_Amp__A, data.L3_Amp__A, data.N_Amp__A
    ];
    return Boolean(data.Milliseconds?.length && channels.some((values) => Boolean(values?.length)));
  }

  function recordBenchmarkPaint(paintGeneration: number) {
    const benchmarkWindow = window as Window & { __pqBenchmark?: unknown };
    if (benchmarkWindow.__pqBenchmark) {
      window.dispatchEvent(new CustomEvent('pq:panel-paint', {
        detail: { panelId: plot.id, generation: paintGeneration }
      }));
    }
  }

  async function loadData(key: string, request: PlotRequest) {
    lastDataKey = key;
    response = null;
    responseKey = '';
    onAnalysisData(plot.id, null);
    onNavigation({ panelId: plot.id, previous: null, next: null, resolved: null });
    controller?.abort();
    controller = new AbortController();
    const localController = controller;
    const currentGeneration = ++generation;
    loading = true;
    error = '';
    try {
      if (!effectiveMonitor) throw new Error('The selected monitoring location is unavailable.');
      let nextResponse: ApiEnvelope;
      if (request.batchBase && request.columns) {
        nextResponse = await rmsBatcher.request(request.batchBase, request.columns, localController.signal);
      } else {
        try {
          nextResponse = await requestManager.latestJson<ApiEnvelope>(`panel:${plot.id}`, request.url, { signal: localController.signal, priority: 'visible' });
        } catch (reason) {
          if (reason instanceof HttpError && reason.status === 404 && request.url.endsWith('/navigate')) {
            nextResponse = await requestManager.latestJson<ApiEnvelope>(`panel:${plot.id}`, request.url.replace(/\/navigate$/, ''), { signal: localController.signal, priority: 'visible' });
          } else throw reason;
        }
      }
      if (currentGeneration !== generation || localController.signal.aborted) return;
      if (nextResponse.error && nextResponse.error !== 'none') throw new Error(nextResponse.error);

      recordBenchmarkPaint(currentGeneration);
      response = nextResponse;
      responseKey = key;
      onNavigation({
        panelId: plot.id,
        previous: nextResponse.navigation?.previous_timestamp ?? null,
        next: nextResponse.navigation?.next_timestamp ?? null,
        resolved: nextResponse.navigation?.resolved_timestamp ?? null
      });
    } catch (reason) {
      if (localController.signal.aborted || currentGeneration !== generation || (reason instanceof DOMException && reason.name === 'AbortError')) return;
      if (reason instanceof HttpError && reason.status === 503) error = 'The server is busy. This plot can be retried after capacity becomes available.';
      else if (reason instanceof HttpError && reason.status === 504) error = 'The query exceeded the server time limit. Narrow the range and retry.';
      else error = reason instanceof Error ? reason.message : 'Unable to load this plot.';
    } finally {
      if (currentGeneration === generation) loading = false;
    }
  }

  async function loadEvents(key: string) {
    lastEventKey = key;
    const currentGeneration = ++eventGeneration;
    eventController?.abort();
    eventController = null;
    eventData = null;
    eventListError = '';
    eventLoading = false;
    if (!key) return;
    eventController = new AbortController();
    const localController = eventController;
    eventLoading = true;
    try {
      const events = await requestManager.latestJson<Record<string, EventMarkerItem[]>>(
        `events:${plot.id}`,
        `/events/${encodeURIComponent(key)}?include_analysis=1`,
        { signal: localController.signal, priority: 'visible', reuseCompleted: true }
      );
      if (currentGeneration === eventGeneration && !localController.signal.aborted) {
        eventData = events;
        eventListError = '';
      }
    } catch (reason) {
      if (!localController.signal.aborted && currentGeneration === eventGeneration) {
        eventListError = reason instanceof Error ? reason.message : 'Events could not be loaded.';
      }
    } finally {
      if (currentGeneration === eventGeneration) eventLoading = false;
    }
  }

  function selectEvent(timestampMilliseconds: number) {
    const timestampSeconds = Math.round(timestampMilliseconds / 1000);
    const drilldownHeight = eventDrilldownElement?.getBoundingClientRect().height ?? 0;
    if (drilldownHeight > 0) eventDrilldownLoadingMinHeight = Math.ceil(drilldownHeight);
    onFocused();
    onEventSelect(effectiveMonitorId, timestampSeconds);
  }

  function eventNavigationKeyIsFromControl(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    if (target.closest('[data-pq-event-marker]')) return false;
    const editable = target.closest('[contenteditable]');
    if (target.closest('input, select, textarea')
      || (editable && editable.getAttribute('contenteditable') !== 'false')) return true;
    if (target.closest('.event-drilldown')) return false;
    return Boolean(target.closest('button, summary, a[href], [role="button"]'));
  }

  function navigateSelectedEvent(event: KeyboardEvent) {
    if (
      !keyboardActive
      || !selectedEvent
      || event.defaultPrevented
      || event.isComposing
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.shiftKey
      || eventNavigationKeyIsFromControl(event.target)
      || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')
    ) return;
    event.preventDefault();
    const orderedTimestamps = event.key === 'ArrowLeft'
      ? [...navigableEventTimestamps].reverse()
      : navigableEventTimestamps;
    const target = orderedTimestamps.find((timestamp) => event.key === 'ArrowLeft'
      ? timestamp < selectedEvent.timestamp
      : timestamp > selectedEvent.timestamp);
    if (target == null) return;
    selectEvent(target * 1000);
  }

  async function loadEventWaveforms(timestampSeconds: number, key: string) {
    lastEventWaveformKey = key;
    eventWaveformRange = undefined;
    eventWaveformController?.abort();
    eventWaveformData = null;
    eventWaveforms = [];
    eventWaveformError = '';
    eventWaveformMissing = false;
    onWaveformAnalysisData(plot.id, null);
    eventWaveformController = new AbortController();
    const localController = eventWaveformController;
    const currentGeneration = ++waveformGeneration;
    eventWaveformLoading = true;
    try {
      const data = await requestManager.latestJson<EventWaveformResponse>(
        `event-waveform:${plot.id}`,
        `/event/${encodeURIComponent(effectiveMonitorId)}/${timestampSeconds}?include_analysis=1`,
        { signal: localController.signal, priority: 'visible' }
      );
      if (currentGeneration === waveformGeneration && !localController.signal.aborted) {
        eventWaveformData = { data, timestampSeconds };
        if (hasCapturedEventWaveform(data)) {
          renderedEventNeutral = plot.options.showNeutral;
          eventWaveforms = eventWaveformModels(data, timestampSeconds, renderedEventNeutral);
          eventWaveformMissing = false;
          publishWaveformAnalysis(eventWaveforms, timestampSeconds);
        } else {
          eventWaveforms = [];
          eventWaveformMissing = true;
          publishWaveformAnalysis([], timestampSeconds, 'No captured event waveform is available for this event.');
        }
        eventWaveformError = '';
      }
    } catch (reason) {
      if (!localController.signal.aborted && currentGeneration === waveformGeneration) {
        eventWaveformError = reason instanceof Error ? reason.message : 'The captured waveform could not be loaded.';
        publishWaveformAnalysis([], timestampSeconds, eventWaveformError);
      }
    } finally {
      if (currentGeneration === waveformGeneration) {
        eventWaveformLoading = false;
        eventDrilldownLoadingMinHeight = 0;
      }
    }
  }

  function publishWaveformAnalysis(models: ChartModel[], timestampSeconds: number, emptyMessage?: string) {
    const waveformClassification = eventWaveformData
      ? eventAnalysisFromResponse(eventWaveformData.data)?.estimatedCause
      : undefined;
    const markerClassification = selectedEventMarker
      ? eventClassificationForMarker(selectedEventMarker.analysis).label
      : undefined;
    onWaveformAnalysisData(plot.id, {
      plotId: plot.id,
      monitorId: effectiveMonitorId,
      monitorName: effectiveMonitorName,
      eventClassification: waveformClassification ?? markerClassification,
      model: {
        ...eventWaveformAnalysisModel(models, emptyMessage),
        subtitle: formatUtc(timestampSeconds, true)
      }
    });
  }

  function resetEventWaveforms() {
    waveformGeneration += 1;
    eventWaveformController?.abort();
    eventWaveformController = null;
    eventWaveformData = null;
    eventWaveforms = [];
    eventWaveformError = '';
    eventWaveformMissing = false;
    eventWaveformLoading = false;
    eventDrilldownLoadingMinHeight = 0;
    eventWaveformRange = undefined;
    lastEventWaveformKey = '';
    onWaveformAnalysisData(plot.id, null);
  }

  function clearSelectedEvent() {
    resetEventWaveforms();
    if (plot.selectedEvent) update({ selectedEvent: undefined });
  }

  function retry() {
    lastDataKey = '';
    if (dataRequest) void loadData(dataKey, dataRequest);
  }

  function retryEvents() {
    if (eventKey) void loadEvents(eventKey);
  }

  function retryEventWaveform() {
    if (selectedEvent && selectedEventKey) void loadEventWaveforms(selectedEvent.timestamp, selectedEventKey);
  }

  function chartRangeChanged(range: [number, number]) {
    if (plot.linkedTime) onRangeChange(range);
    else update({ rangeOverride: range });
  }

  function resetMainPlotRange() {
    if (definition.timeDomain !== 'range' || !rangeResetMonitor) return;
    if (plot.linkedTime) {
      onResetRange();
      return;
    }
    const range: [number, number] = [rangeResetMonitor.earliest_date, rangeResetMonitor.latest_date];
    update({
      rangeOverride: range,
      cursorOverride: clampTimestamp(plot.cursorOverride ?? workspace.cursor, range)
    });
  }

  function becameVisible(value: boolean) {
    visible = value;
  }

  onDestroy(() => {
    generation += 1;
    eventGeneration += 1;
    waveformGeneration += 1;
    controller?.abort();
    eventController?.abort();
    eventWaveformController?.abort();
    requestManager.nextGeneration(`panel:${plot.id}`);
    onAnalysisData(plot.id, null);
    onWaveformAnalysisData(plot.id, null);
  });
</script>

<svelte:window on:keydown={navigateSelectedEvent} />

<article
  class="plot-panel"
  use:visibility={{ rootMargin: '800px 0px', onChange: becameVisible }}
  on:focusin={onFocused}
  on:pointerdown={onFocused}
  data-plot-id={plot.id}
>
  <PlotPanelHeader
    {index}
    {count}
    parameterName={parameter.name}
    plotName={definition.shortName}
    {plot}
    {parameter}
    {definition}
    selectedEventVisible={Boolean(selectedEvent)}
    {eventClassificationChoices}
    {hiddenEventClassificationKeys}
    {visibleEventClassificationCount}
    locationName={comparisonMode === 'multiple-locations'
      ? effectiveMonitor
        ? `${effectiveMonitor.primary_name}, ${effectiveMonitor.monitor_name_formatted}`
        : effectiveMonitorId
      : undefined}
    independentTime={!plot.linkedTime}
    {duplicateDisabled}
    {onDuplicate}
    {onMove}
    {onRemove}
    onSetOption={setOption}
    onSetEventClassificationVisible={setEventClassificationVisible}
    onShowAllEventClassifications={showAllEventClassifications}
    onHideAllEventClassifications={hideAllEventClassifications}
  />

  <PlotPanelControls
    {plot}
    {workspace}
    {monitors}
    {comparisonMode}
    {effectiveMonitorId}
    {parameter}
    {definition}
    {onUpdate}
  />
  {#if eventListError}
    <div class="inline-warning inline-warning--action" role="alert">
      <p>{eventListError}</p>
      <button class="button button--quiet" type="button" on:click={retryEvents}><RefreshCw size={17} aria-hidden="true" /> Retry events</button>
    </div>
  {/if}

  <div class="plot-panel__content" aria-busy={panelLoading}>
    {#if !visible}
      <div class="plot-placeholder">This plot will load as it approaches the viewport.</div>
    {:else if model}
      <div class:plot-with-histogram={histogram} class:plot-layout--concealed={Boolean(error)} class="plot-layout">
        <div class="plot-layout__main">
          <ChartHost
            {model}
            kind={plot.kind}
            showTitle={false}
            selectedEventTimestamp={selectedEvent?.timestamp}
            cursor={definition.timeDomain === 'heatmap' ? startOfUtcDay(markerCursor) : definition.timeDomain === 'range' ? markerCursor : undefined}
            onRangeChange={definition.timeDomain === 'range' ? chartRangeChanged : undefined}
            onResetZoom={definition.timeDomain === 'range' ? resetMainPlotRange : undefined}
            {resetZoomVisible}
            onCursorPreview={plot.linkedTime && definition.timeDomain === 'range' ? onCursorPreview : undefined}
            synchronizedRange={definition.timeDomain === 'range' ? (plot.linkedTime ? workspace.range : plot.rangeOverride) : undefined}
          />
          {#if hasNoData(model)}<p class="panel-message" role="status">{model.emptyMessage}</p>{/if}
        </div>
        {#if histogram}<div class="plot-layout__histogram"><ChartHost model={histogram} kind="phase-series" compact showTitle={false} zoomEnabled={false} /></div>{/if}
      </div>
      {#if error}
        <div class="panel-error panel-error--overlay" role="alert">
          <p>{error}</p>
          <button class="button" type="button" on:click={retry}><RefreshCw size={17} aria-hidden="true" /> Retry</button>
        </div>
      {/if}
    {:else if error}
      <div class="panel-error" role="alert">
        <p>{error}</p>
        <button class="button" type="button" on:click={retry}><RefreshCw size={17} aria-hidden="true" /> Retry</button>
      </div>
    {:else}
      <div class="plot-placeholder" aria-hidden="true"></div>
    {/if}
  </div>
  {#if selectedEvent && (eventWaveformLoading || eventWaveforms.length || eventWaveformError || eventWaveformMissing)}
    <div class="panel-transition panel-transition--event" transition:panelSlide={{ preserveScroll: true }}>
      <EventDrilldown
        plotId={plot.id}
        timestampSeconds={eventWaveformData?.timestampSeconds ?? selectedEvent.timestamp}
        {keyboardActive}
        loading={eventWaveformLoading}
        loadingMinHeight={eventDrilldownLoadingMinHeight}
        error={eventWaveformError}
        missing={eventWaveformMissing}
        analysis={eventAnalysis}
        analysisHasDetails={eventAnalysisHasDetails}
        models={eventWaveforms}
        bind:range={eventWaveformRange}
        bind:element={eventDrilldownElement}
        onClose={clearSelectedEvent}
        onRetry={retryEventWaveform}
      />
    </div>
  {/if}
</article>
